import { StrokeTextureFilter, strokeFilterId, type StrokeTexture } from 'ai-tutor-player';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DESIGN_CANVAS, useCanvasScale } from '@/lib/canvas';
import { effectiveFreeformRect } from '@/lib/freeform';
import type { Annotation, DrawStroke, Slide, Spotlight } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

import { strokePointsAttr } from './draw-playback';
import { StageElement } from './StageElement';
import type { TeachTool } from './TeachStudio';

interface Props {
  slide: Slide;
  /** Annotations to render on top of the elements (caller controls accumulation). */
  annotations: Annotation[];
  /** Freehand strokes to render (caller controls accumulation, like annotations). */
  drawStrokes?: DrawStroke[];
  /**
   * When non-null, restricts visible elements to this set (used during playback).
   * `null` means all elements on the slide are visible (edit mode).
   */
  revealedElementIds: Set<string> | null;
  tool: TeachTool;
  onPickElement: (elementId: string) => void;
  /** Called when a pen/highlighter stroke is finished (design-space points). */
  onStrokeComplete?: (stroke: Omit<DrawStroke, 'id'>) => void;
  /** Media texture applied to NEW pen/marker strokes (absent = smooth ink). */
  penTexture?: StrokeTexture;
  /** Spotlights — in preview, the first active one zooms+vignettes; in edit they
   *  draw as dashed outlines (the selected step's spotlights). */
  spotlights?: Spotlight[];
  previewing?: boolean;
  /** Called when the spotlight tool clicks the slide (design-space point). */
  onPlaceSpotlight?: (x: number, y: number) => void;
  /** Edit-mode drag/resize of a spotlight region. */
  onUpdateSpotlight?: (id: string, patch: Partial<Spotlight>) => void;
}

const PEN = { color: '#0ea5e9', width: 3 };
const HIGHLIGHTER = { color: '#fde047', width: 16 };

interface ElementBox {
  id: string;
  rect: { left: number; top: number; width: number; height: number };
}

export function Stage({
  slide,
  annotations,
  drawStrokes = [],
  revealedElementIds,
  tool,
  onPickElement,
  onStrokeComplete,
  penTexture,
  spotlights = [],
  previewing = false,
  onPlaceSpotlight,
  onUpdateSpotlight,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [boxes, setBoxes] = useState<Record<string, ElementBox['rect']>>({});
  // The stage is rendered scaled (ScaledStage). getBoundingClientRect returns
  // screen px, but the annotation overlay lives in the scaled design space, so
  // divide measured rects by the scale to keep marks aligned with elements.
  const scale = useCanvasScale();

  // Per-element write-on duration = the line's SPOKEN span (stored `writeMs`),
  // so the pen paces to the narration; falls back to the gap until the next
  // element is cued. Mirrors the shared player's SlideStage so teach playback
  // writes at the same speed the classroom will (user 2026-08-14).
  const writeDurations = useMemo(() => {
    const trig: Array<{ id: string; t: number }> = [];
    const stored: Record<string, number> = {};
    ((slide.revealSteps ?? []) as { elementTimings?: Record<string, { triggerMs?: number; writeMs?: number }> }[]).forEach((st) => {
      const et = st.elementTimings ?? {};
      for (const [id, t] of Object.entries(et)) {
        if (typeof t?.triggerMs === 'number') trig.push({ id, t: t.triggerMs });
        if (typeof t?.writeMs === 'number') stored[id] = t.writeMs;
      }
    });
    trig.sort((a, b) => a.t - b.t);
    const m: Record<string, number> = {};
    for (let i = 0; i < trig.length; i++) {
      const id = trig[i].id;
      if (typeof stored[id] === 'number') m[id] = Math.max(500, Math.min(stored[id], 16000));
      else {
        const next = i + 1 < trig.length ? trig[i + 1].t : trig[i].t + 4000;
        m[id] = Math.max(900, Math.min(next - trig[i].t, 12000));
      }
    }
    return m;
  }, [slide]);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const next: Record<string, ElementBox['rect']> = {};
    elementRefs.current.forEach((node, id) => {
      const r = node.getBoundingClientRect();
      next[id] = {
        left: (r.left - containerRect.left) / scale,
        top: (r.top - containerRect.top) / scale,
        width: r.width / scale,
        height: r.height / scale,
      };
    });
    setBoxes(next);
  }, [scale]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    elementRefs.current.forEach((n) => ro.observe(n));
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, slide]);

  const registerElement = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) elementRefs.current.set(id, node);
      else elementRefs.current.delete(id);
    },
    [],
  );

  const drawing = tool === 'pen' || tool === 'highlighter';
  // Element-picking (annotation) tools — pen/highlighter draw freely, spotlight
  // places a region, both handled separately.
  const interactive = tool !== 'cursor' && !drawing && tool !== 'spotlight';

  // Active spotlight (preview) → gently zoom+center its region; a soft vignette
  // caves in. When it ends we keep showing it briefly in an "out" phase so the
  // zoom-out + vignette cave-out ease with the SAME duration as the entrance
  // (otherwise the exit looks rushed).
  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const activeSp = previewing ? spotlights[0] : null;
  const [display, setDisplay] = useState<{ sp: Spotlight; phase: 'in' | 'out' } | null>(null);
  const displayRef = useRef<{ sp: Spotlight; phase: 'in' | 'out' } | null>(null);
  displayRef.current = display;
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (activeSp) {
      if (exitTimer.current) clearTimeout(exitTimer.current);
      exitTimer.current = null;
      setDisplay({ sp: activeSp, phase: 'in' });
      return;
    }
    const cur = displayRef.current;
    if (!cur || cur.phase === 'out') return;
    setDisplay({ sp: cur.sp, phase: 'out' });
    exitTimer.current = setTimeout(() => setDisplay(null), (cur.sp.easeMs ?? 700) + 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSp?.id, activeSp?.w, activeSp?.h, activeSp?.zoom, activeSp?.easeMs, activeSp?.x, activeSp?.y]);

  const dsp = display?.sp ?? null;
  const exiting = display?.phase === 'out';
  const easeMs = dsp?.easeMs ?? 700;
  const zoomedIn = !!dsp && !exiting;
  const z = zoomedIn ? dsp.zoom ?? 1.25 : 1;
  // Vignette gradient stays sized to the zoomed region across in/out so it
  // doesn't pop; the cave animation handles the motion.
  const vz = dsp ? dsp.zoom ?? 1.25 : 1;
  const ocx = dsp ? dsp.x + dsp.w / 2 : DESIGN_CANVAS.width / 2;
  const ocy = dsp ? dsp.y + dsp.h / 2 : DESIGN_CANVAS.height / 2;
  const zoomStyle: React.CSSProperties = {
    transform: zoomedIn
      ? `translate(${DESIGN_CANVAS.width / 2 - ocx}px, ${DESIGN_CANVAS.height / 2 - ocy}px) scale(${z})`
      : 'none',
    transformOrigin: `${ocx}px ${ocy}px`,
    transition: `transform ${easeMs}ms ${EASE}`,
  };

  const [livePts, setLivePts] = useState<{ x: number; y: number }[] | null>(null);
  const drawingRef = useRef(false);

  const toDesignPoint = (e: React.PointerEvent) => {
    const c = containerRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) / scale, y: (e.clientY - r.top) / scale };
  };

  const onDrawDown = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    setLivePts([toDesignPoint(e)]);
  };
  const onDrawMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    setLivePts((prev) => (prev ? [...prev, toDesignPoint(e)] : [toDesignPoint(e)]));
  };
  const onDrawUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const pts = livePts ?? [];
    setLivePts(null);
    if (pts.length < 2) return;
    const cfg = tool === 'highlighter' ? HIGHLIGHTER : PEN;
    onStrokeComplete?.({
      rowId: slide.rows[0]?.id ?? '',
      points: pts,
      color: cfg.color,
      strokeWidth: cfg.width,
      texture: penTexture,
    });
  };

  return (
    // The stage is a landscape surface that grows with its content (ScaledStage
    // scales it to width; the parent scrolls when it's taller than the frame).
    <div ref={containerRef} className="relative w-full">
      <div
        style={{ background: slide.background ?? '#ffffff', minHeight: DESIGN_CANVAS.height }}
        className="relative w-full overflow-hidden rounded-2xl bg-white shadow-slide ring-1 ring-black/5"
      >
       {/* Zoom wrapper — driven by an active spotlight, else identity. */}
       <div style={zoomStyle}>
        <div className="absolute left-6 top-5 z-10 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
          {slide.label}
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
        </div>

        <div className="flex w-full flex-col pt-12 pb-6" style={{ minHeight: DESIGN_CANVAS.height }}>
          {slide.rows.map((row) => {
            // Every column is a freeform canvas (mirrors the editor): columns sit
            // side by side at their widthPercent, sharing the row height, and each
            // element is absolutely positioned via effectiveFreeformRect.
            const heightPx = Math.max(120, (row.heightPercent / 100) * DESIGN_CANVAS.height);
            return (
              <div
                key={row.id}
                className="flex w-full gap-3"
                style={{
                  paddingTop: row.padding.top,
                  paddingRight: row.padding.right,
                  paddingBottom: row.padding.bottom,
                  paddingLeft: row.padding.left,
                }}
              >
                {row.columns.map((col) => (
                  <div
                    key={col.id}
                    style={{ width: `${col.widthPercent}%`, height: heightPx }}
                    className="relative overflow-hidden"
                  >
                    {col.elements.map((el, i) => {
                      const visible =
                        revealedElementIds === null || revealedElementIds.has(el.id);
                      const r = effectiveFreeformRect(el.position, i);
                      return (
                        <div
                          key={el.id}
                          ref={registerElement(el.id)}
                          data-element-id={el.id}
                          onClick={(e) => {
                            if (!interactive || !visible) return;
                            e.stopPropagation();
                            onPickElement(el.id);
                          }}
                          style={{
                            position: 'absolute',
                            left: r.x,
                            top: r.y,
                            width: r.w,
                            height: r.h,
                            transform: r.rotation ? `rotate(${r.rotation}deg)` : undefined,
                            zIndex: r.zIndex,
                          }}
                          className={cn(
                            'rounded-md transition-all duration-300',
                            // Images carry their own drop-shadow, and handwritten
                            // text shows a pencil that reaches above the top line —
                            // clipping chops either off. Everything else clips.
                            el.type === 'image' ||
                              (previewing &&
                                ((el.type === 'text' && el.handwritten !== false) ||
                                  el.type === 'latex'))
                              ? 'overflow-visible'
                              : 'overflow-hidden',
                            interactive && visible
                              ? 'cursor-crosshair ring-1 ring-dashed ring-accent/20 hover:ring-accent/60'
                              : '',
                            !visible && 'pointer-events-none opacity-0 translate-y-1',
                          )}
                        >
                          <StageElement
                            element={el}
                            fill
                            previewing={previewing}
                            revealed={visible}
                            writeMs={writeDurations[el.id]}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
       </div>
       {/* end zoom wrapper */}

       {/* Spotlight vignette (preview): a soft black→alpha gradient that leaves the
           focused region clear and fades darkness in toward the edges. The region
           is centered (the content zoomed to it), so the gradient sits at center. */}
       {dsp && (
         <div
           // `key` ties the cave animation to the region + phase so entering the
           // exit phase replays it as a cave-OUT.
           key={`${dsp.id}:${exiting ? 'out' : 'in'}`}
           className="pointer-events-none absolute z-20"
           style={{
             // Oversized (so its own box edge stays off the slide) + blurred so
             // the clear→dark transition is un-resolvable (no perceptible circle
             // edge). The fully-clear core is kept WIDER than the spotlight region
             // so the blur's inward bleed never dims the focused content. Tops out
             // at 30% black → surroundings stay ≥70% opaque.
             inset: -160,
             filter: 'blur(56px)',
             background: `radial-gradient(ellipse ${dsp.w * vz * 1.18}px ${dsp.h * vz * 1.18}px at 50% 50%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.12) 78%, rgba(0,0,0,0.22) 90%, rgba(0,0,0,0.3) 100%)`,
             transformOrigin: '50% 50%',
             animation: `${exiting ? 'spotlight-cave-out' : 'spotlight-cave-in'} ${easeMs}ms ${EASE} both`,
           }}
         />
       )}

       {/* Spotlight regions (edit): draggable + resizable outlines. */}
       {!previewing &&
         spotlights.map((s) => (
           <SpotlightOutline key={s.id} sp={s} scale={scale} onUpdate={onUpdateSpotlight} />
         ))}

       {/* Spotlight tool — click the slide to drop a circle there. */}
       {tool === 'spotlight' && (
         <div
           className="absolute inset-0 z-30 cursor-crosshair"
           onPointerDown={(e) => {
             const p = toDesignPoint(e);
             onPlaceSpotlight?.(p.x, p.y);
           }}
         />
       )}
      </div>

      {/* Annotation overlay */}
      <div className="pointer-events-none absolute inset-0">
        {annotations.map((a) => {
          const b = boxes[a.targetElementId];
          if (!b) return null;
          if (revealedElementIds && !revealedElementIds.has(a.targetElementId)) return null;
          return <AnnotationMark key={a.id} annotation={a} rect={b} />;
        })}
      </div>

      {/* Freehand draw overlay — captures pointer only while a pen tool is on.
          Explicit viewBox + `xMinYMin meet` pins a 1:1 design-px coordinate system
          top-left in every engine (WebKit/Safari mis-mapped the no-viewBox,
          percentage-sized SVG, shifting strokes off their targets). `overflow:
          visible` so edge strokes aren't clipped (was defaulting to hidden). */}
      <svg
        width={DESIGN_CANVAS.width}
        height={DESIGN_CANVAS.height}
        viewBox={`0 0 ${DESIGN_CANVAS.width} ${DESIGN_CANVAS.height}`}
        preserveAspectRatio="xMinYMin meet"
        className="absolute inset-0 h-full w-full"
        style={{
          overflow: 'visible',
          pointerEvents: drawing ? 'auto' : 'none',
          cursor: drawing ? 'crosshair' : 'default',
        }}
        onPointerDown={onDrawDown}
        onPointerMove={onDrawMove}
        onPointerUp={onDrawUp}
      >
        {drawStrokes.map((s) => (
          <StrokePath key={s.id} stroke={s} previewing={previewing} />
        ))}
        {livePts && livePts.length > 1 && (
          <g>
            {penTexture && <StrokeTextureFilter strokeId="live" texture={penTexture} />}
            <polyline
              points={livePts.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={tool === 'highlighter' ? HIGHLIGHTER.color : PEN.color}
              strokeWidth={tool === 'highlighter' ? HIGHLIGHTER.width : PEN.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={tool === 'highlighter' ? 0.4 : 1}
              filter={penTexture ? `url(#${strokeFilterId('live')})` : undefined}
            />
          </g>
        )}
      </svg>
    </div>
  );
}

/** Total polyline length (design px) — sets the dash length for the draw-on. */
function polylineLength(points: { x: number; y: number }[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return Math.max(1, len);
}

/** A pen/marker stroke. During playback it "draws on" (traces start→end via a
 *  dash animation); in edit mode it's static. */
function StrokePath({ stroke, previewing }: { stroke: DrawStroke; previewing: boolean }) {
  const len = previewing ? polylineLength(stroke.points) : 0;
  const isMarker = stroke.strokeWidth >= 12;
  // Trace speed: ~1px/ms, clamped, slower-feeling for a natural hand.
  const dur = Math.min(1600, Math.max(280, Math.round(len * 0.9)));
  const drawStyle: React.CSSProperties = previewing
    ? {
        strokeDasharray: len,
        ['--stroke-len' as string]: String(len),
        animation: `stroke-draw ${dur}ms cubic-bezier(0.25, 0.6, 0.3, 1) both`,
      }
    : {};
  const texture = stroke.texture;
  return (
    <g>
      {texture && <StrokeTextureFilter strokeId={stroke.id} texture={texture} />}
      <polyline
        points={strokePointsAttr(stroke)}
        fill="none"
        stroke={stroke.color}
        strokeWidth={stroke.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={isMarker ? 0.4 : 1}
        filter={texture ? `url(#${strokeFilterId(stroke.id)})` : undefined}
        style={drawStyle}
      />
    </g>
  );
}

type SpotMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

/** Draggable + resizable spotlight region (edit mode). Coordinates are design-px;
 *  screen deltas are divided by the canvas scale. */
function SpotlightOutline({
  sp,
  scale,
  onUpdate,
}: {
  sp: Spotlight;
  scale: number;
  onUpdate?: (id: string, patch: Partial<Spotlight>) => void;
}) {
  const [live, setLive] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const drag = useRef<{
    mode: SpotMode;
    clientX0: number;
    clientY0: number;
    x0: number;
    y0: number;
    w0: number;
    h0: number;
  } | null>(null);
  const liveRef = useRef<typeof live>(null);

  useEffect(() => {
    const move = (ev: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = (ev.clientX - d.clientX0) / scale;
      const dy = (ev.clientY - d.clientY0) / scale;
      let { x0: x, y0: y, w0: w, h0: h } = d;
      const MIN = 60;
      if (d.mode === 'move') {
        x = d.x0 + dx;
        y = d.y0 + dy;
      } else {
        if (d.mode === 'se') {
          w = Math.max(MIN, d.w0 + dx);
          h = Math.max(MIN, d.h0 + dy);
        } else if (d.mode === 'sw') {
          w = Math.max(MIN, d.w0 - dx);
          h = Math.max(MIN, d.h0 + dy);
          x = d.x0 + (d.w0 - w);
        } else if (d.mode === 'ne') {
          w = Math.max(MIN, d.w0 + dx);
          h = Math.max(MIN, d.h0 - dy);
          y = d.y0 + (d.h0 - h);
        } else {
          // nw
          w = Math.max(MIN, d.w0 - dx);
          h = Math.max(MIN, d.h0 - dy);
          x = d.x0 + (d.w0 - w);
          y = d.y0 + (d.h0 - h);
        }
      }
      x = Math.max(0, Math.min(DESIGN_CANVAS.width - w, x));
      y = Math.max(0, Math.min(DESIGN_CANVAS.height - h, y));
      const next = { x, y, w, h };
      liveRef.current = next;
      setLive(next);
    };
    const up = () => {
      if (!drag.current) return;
      drag.current = null;
      const v = liveRef.current;
      liveRef.current = null;
      setLive(null);
      if (v) onUpdate?.(sp.id, { x: Math.round(v.x), y: Math.round(v.y), w: Math.round(v.w), h: Math.round(v.h) });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [scale, sp.id, onUpdate]);

  const begin = (mode: SpotMode) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    drag.current = { mode, clientX0: e.clientX, clientY0: e.clientY, x0: sp.x, y0: sp.y, w0: sp.w, h0: sp.h };
  };

  const r = live ?? { x: sp.x, y: sp.y, w: sp.w, h: sp.h };
  const handle = (mode: SpotMode, pos: string) => (
    <span
      onPointerDown={begin(mode)}
      className={cn('absolute h-3 w-3 rounded-full border-2 border-white bg-amber-500 shadow', pos)}
    />
  );
  return (
    <div
      onPointerDown={begin('move')}
      className="absolute z-30 cursor-move border-2 border-dashed border-amber-500"
      style={{ left: r.x, top: r.y, width: r.w, height: r.h, borderRadius: '50%' }}
    >
      {handle('nw', '-left-1.5 -top-1.5 cursor-nwse-resize')}
      {handle('ne', '-right-1.5 -top-1.5 cursor-nesw-resize')}
      {handle('sw', '-left-1.5 -bottom-1.5 cursor-nesw-resize')}
      {handle('se', '-right-1.5 -bottom-1.5 cursor-nwse-resize')}
    </div>
  );
}

function AnnotationMark({
  annotation,
  rect,
}: {
  annotation: Annotation;
  rect: { left: number; top: number; width: number; height: number };
}) {
  const color = annotation.color ?? '#facc15';

  if (annotation.kind === 'highlight') {
    return (
      <div
        className="absolute rounded-md mix-blend-multiply"
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          background: color,
          opacity: 0.35,
        }}
      />
    );
  }
  if (annotation.kind === 'underline') {
    return (
      <div
        className="absolute"
        style={{
          left: rect.left + 4,
          top: rect.top + rect.height - 2,
          width: rect.width - 8,
          height: 3,
          background: color,
          borderRadius: 2,
        }}
      />
    );
  }
  if (annotation.kind === 'encircle') {
    return (
      <div
        className="absolute rounded-full border-[3px]"
        style={{
          left: rect.left - 8,
          top: rect.top - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderColor: color,
          borderStyle: 'dashed',
        }}
      />
    );
  }
  if (annotation.kind === 'annotate') {
    return (
      <div
        className="absolute"
        style={{
          left: rect.left + rect.width + 12,
          top: rect.top,
          maxWidth: 220,
        }}
      >
        <div
          className="rounded-md px-2.5 py-1.5 text-[11px] font-medium text-white shadow-floating"
          style={{ background: color }}
        >
          {annotation.text || 'Note'}
        </div>
      </div>
    );
  }
  return null;
}
